/**
 * MarkdownContent - Reusable component for rendering markdown-like content
 * 
 * Handles:
 * - Line breaks (newlines)
 * - Headers (## and ###)
 * - Bold text (**text**)
 * - Inline code (`code`)
 * - Code blocks (```code```)
 * - Bullet points (- item)
 * - Numbered lists (1. item)
 * - Links [text](url)
 * - Tables (| col1 | col2 |)
 */
import { memo, useMemo } from 'react'
import { cn } from '@/lib/utils'

export interface MarkdownContentProps {
  /** The markdown content to render */
  content: string
  /** Additional CSS classes */
  className?: string
  /** Size variant */
  variant?: 'default' | 'compact'
  /** Use inverted colors (for user messages on primary background) */
  inverted?: boolean
}

/**
 * Escape HTML entities to prevent XSS
 */
function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')
}

/**
 * Render markdown-like content to HTML
 */
function renderMarkdown(content: string): string {
  // First escape HTML
  let html = escapeHtml(content)

  // Handle code blocks first (```...```)
  html = html.replace(/```(\w+)?\n([\s\S]*?)```/g, (_, _lang, code) => {
    return `<pre class="bg-muted p-3 rounded-md overflow-x-auto my-2 whitespace-pre-wrap break-words text-sm"><code class="font-mono">${code.trim()}</code></pre>`
  })

  // Handle tables - look for lines starting and ending with |
  html = html.replace(/^(\|.+\|)\n(\|[-:| ]+\|)\n((?:\|.+\|\n?)+)/gm, (_, headerRow, _separatorRow, bodyRows) => {
    // Parse header cells
    const headers = headerRow.split('|').filter((cell: string) => cell.trim() !== '')
    const headerCells = headers.map((h: string) => 
      `<th class="border border-border px-3 py-2 text-left font-semibold bg-muted">${h.trim()}</th>`
    ).join('')
    
    // Parse body rows
    const rows = bodyRows.trim().split('\n')
    const bodyHtml = rows.map((row: string) => {
      const cells = row.split('|').filter((cell: string) => cell.trim() !== '')
      const cellsHtml = cells.map((c: string) => 
        `<td class="border border-border px-3 py-2">${c.trim()}</td>`
      ).join('')
      return `<tr class="hover:bg-muted/50">${cellsHtml}</tr>`
    }).join('')
    
    return `<div class="overflow-x-auto my-3"><table class="w-full border-collapse text-sm"><thead><tr>${headerCells}</tr></thead><tbody>${bodyHtml}</tbody></table></div>`
  })

  // Handle inline code (`code`)
  html = html.replace(/`([^`]+)`/g, '<code class="bg-muted px-1.5 py-0.5 rounded text-sm font-mono">$1</code>')

  // Handle headers
  html = html.replace(/^### (.+)$/gm, '<h3 class="font-semibold text-base mt-4 mb-2">$1</h3>')
  html = html.replace(/^## (.+)$/gm, '<h2 class="font-semibold text-lg mt-4 mb-2">$1</h2>')

  // Handle bold (**text**)
  html = html.replace(/\*\*([^*]+)\*\*/g, '<strong class="font-semibold">$1</strong>')

  // Handle links [text](url)
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-primary underline hover:no-underline" target="_blank" rel="noopener noreferrer">$1</a>')

  // Handle numbered lists (1. item)
  html = html.replace(/^(\d+)\. (.+)$/gm, '<li class="ml-4 list-decimal">$2</li>')

  // Handle bullet points (- item or * item)
  html = html.replace(/^[-*] (.+)$/gm, '<li class="ml-4 list-disc">$1</li>')

  // Wrap consecutive list items
  html = html.replace(/(<li class="ml-4 list-disc">.+<\/li>\n?)+/g, (match) => {
    return `<ul class="my-2 space-y-1">${match}</ul>`
  })
  html = html.replace(/(<li class="ml-4 list-decimal">.+<\/li>\n?)+/g, (match) => {
    return `<ol class="my-2 space-y-1">${match}</ol>`
  })

  // Handle line breaks - convert double newlines to paragraphs
  const paragraphs = html.split(/\n\n+/)
  html = paragraphs
    .map(p => {
      // Don't wrap if it's already a block element
      if (p.startsWith('<h') || p.startsWith('<pre') || p.startsWith('<ul') || p.startsWith('<ol')) {
        return p
      }
      // Convert single newlines to <br> within paragraphs
      const withBreaks = p.replace(/\n/g, '<br />')
      return withBreaks ? `<p class="mb-2">${withBreaks}</p>` : ''
    })
    .join('')

  return html
}

/**
 * Reusable markdown content renderer.
 * 
 * Renders markdown-like content with proper formatting including:
 * - Headers, bold text, code blocks
 * - Lists (bullet and numbered)
 * - Links
 * - Line breaks and paragraphs
 * 
 * @example
 * ```tsx
 * <MarkdownContent 
 *   content="## Title\n\nSome **bold** text and `code`"
 * />
 * ```
 */
export const MarkdownContent = memo(function MarkdownContent({
  content,
  className,
  variant = 'default',
  inverted = false
}: MarkdownContentProps) {
  // Memoize the HTML rendering - this is the expensive operation
  const html = useMemo(() => renderMarkdown(content), [content])

  return (
    <div 
      className={cn(
        'prose prose-sm dark:prose-invert max-w-none',
        'break-words overflow-hidden',
        variant === 'compact' && 'prose-compact',
        // Override prose defaults for better density
        '[&_p]:mb-2 [&_p:last-child]:mb-0',
        '[&_h2]:mt-4 [&_h2]:mb-2 [&_h2]:text-lg',
        '[&_h3]:mt-3 [&_h3]:mb-1.5 [&_h3]:text-base',
        '[&_ul]:my-2 [&_ol]:my-2',
        '[&_li]:my-0.5',
        '[&_pre]:my-2',
        '[&_code]:text-sm',
        // Inverted mode: white text for user messages on primary background
        inverted && [
          'text-primary-foreground',
          '[&_p]:text-primary-foreground',
          '[&_h2]:text-primary-foreground',
          '[&_h3]:text-primary-foreground',
          '[&_li]:text-primary-foreground',
          '[&_strong]:text-primary-foreground',
          '[&_a]:text-primary-foreground [&_a]:underline',
          '[&_code]:bg-primary-foreground/20 [&_code]:text-primary-foreground',
          '[&_pre]:bg-primary-foreground/10 [&_pre]:text-primary-foreground',
          '[&_th]:text-primary-foreground [&_th]:border-primary-foreground/30 [&_th]:bg-primary-foreground/10',
          '[&_td]:text-primary-foreground [&_td]:border-primary-foreground/30',
        ],
        className
      )}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  )
})
